import { useState, useEffect } from 'react';
import {
  Trash2,
  Edit2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronUp,
  ChevronDown,
  Maximize2,
  Minimize2,
  ImageIcon,
} from 'lucide-react';
import { useStore } from '../store';
import type { ImageBlock as ImageBlockType } from '../types/images';

interface ImageBlockProps {
  block: ImageBlockType;
  sectionId: number;
  onEdit?: () => void;
}

const SIZE_CLASSES = {
  small: 'max-w-[200px]',
  medium: 'max-w-[400px]',
  large: 'max-w-[600px]',
  full: 'max-w-full',
};

const ALIGNMENT_CLASSES = {
  left: 'mr-auto',
  center: 'mx-auto',
  right: 'ml-auto',
};

export function ImageBlock({ block, sectionId, onEdit }: ImageBlockProps) {
  const { updateImageBlock, removeImageBlock } = useStore();
  const [isEditing, setIsEditing] = useState(false);
  const [caption, setCaption] = useState(block.caption || '');
  const [showControls, setShowControls] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isNewImage, setIsNewImage] = useState(true);

  // Track when a new image URL is set to trigger reveal animation
  useEffect(() => {
    setIsNewImage(true);
    setImageLoaded(false);
    const timer = setTimeout(() => setIsNewImage(false), 2000);
    return () => clearTimeout(timer);
  }, [block.imageUrl]);

  const handleSizeChange = (size: ImageBlockType['width']) => {
    updateImageBlock(sectionId, block.id, { width: size });
  };

  const handleAlignmentChange = (alignment: ImageBlockType['alignment']) => {
    updateImageBlock(sectionId, block.id, { alignment });
  };

  const handlePositionChange = (position: ImageBlockType['position']) => {
    updateImageBlock(sectionId, block.id, { position });
  };

  const handleCaptionSave = () => {
    updateImageBlock(sectionId, block.id, { caption });
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to remove this image?')) {
      removeImageBlock(sectionId, block.id);
    }
  };

  return (
    <div
      className={`relative group my-4 ${ALIGNMENT_CLASSES[block.alignment]}`}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {/* Image Container */}
      <div className={`relative ${SIZE_CLASSES[block.width]} ${!imageLoaded ? 'image-generating' : ''}`}>
        {/* Loading placeholder */}
        {!imageLoaded && (
          <div className="image-placeholder rounded-lg aspect-video flex flex-col items-center justify-center gap-2">
            <ImageIcon className="w-8 h-8 animate-pulse" />
            <span className="text-sm">Loading image...</span>
          </div>
        )}
        <img
          src={block.imageUrl}
          alt={block.caption || 'Section image'}
          className={`w-full rounded-lg shadow-md ${isNewImage && imageLoaded ? 'image-reveal' : ''} ${!imageLoaded ? 'hidden' : ''}`}
          onLoad={() => setImageLoaded(true)}
        />

        {/* Hover Controls - Clean Toolbar */}
        {showControls && (
          <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center transition-opacity">
            <div className="bg-white rounded-xl shadow-xl p-3 max-w-[280px]">
              {/* Size */}
              <div className="mb-3">
                <label className="text-[10px] font-medium text-gray-500 uppercase mb-1.5 block">Size</label>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  {([
                    { id: 'small', label: 'S', icon: Minimize2 },
                    { id: 'medium', label: 'M', icon: null },
                    { id: 'large', label: 'L', icon: null },
                    { id: 'full', label: 'Full', icon: Maximize2 },
                  ] as const).map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => handleSizeChange(id)}
                      className={`flex-1 py-1.5 px-2 text-xs font-medium flex items-center justify-center gap-1 ${
                        block.width === id
                          ? 'bg-accent text-white'
                          : 'hover:bg-gray-50 text-gray-600'
                      }`}
                    >
                      {Icon && <Icon className="w-3 h-3" />}
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Alignment */}
              <div className="mb-3">
                <label className="text-[10px] font-medium text-gray-500 uppercase mb-1.5 block">Alignment</label>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  {([
                    { id: 'left', label: 'Left', icon: AlignLeft },
                    { id: 'center', label: 'Center', icon: AlignCenter },
                    { id: 'right', label: 'Right', icon: AlignRight },
                  ] as const).map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => handleAlignmentChange(id)}
                      className={`flex-1 py-1.5 px-2 text-xs font-medium flex items-center justify-center gap-1 ${
                        block.alignment === id
                          ? 'bg-accent text-white'
                          : 'hover:bg-gray-50 text-gray-600'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Position */}
              <div className="mb-3">
                <label className="text-[10px] font-medium text-gray-500 uppercase mb-1.5 block">Position</label>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  {([
                    { id: 'before', label: 'Before Text', icon: ChevronUp },
                    { id: 'after', label: 'After Text', icon: ChevronDown },
                  ] as const).map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => handlePositionChange(id)}
                      className={`flex-1 py-1.5 px-2 text-xs font-medium flex items-center justify-center gap-1 ${
                        block.position === id
                          ? 'bg-accent text-white'
                          : 'hover:bg-gray-50 text-gray-600'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-gray-100">
                {onEdit && (
                  <button
                    onClick={onEdit}
                    className="flex-1 py-1.5 px-3 text-xs font-medium text-gray-600 hover:bg-gray-50 rounded-lg flex items-center justify-center gap-1"
                  >
                    <Edit2 className="w-3 h-3" />
                    Replace
                  </button>
                )}
                <button
                  onClick={handleDelete}
                  className="flex-1 py-1.5 px-3 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg flex items-center justify-center gap-1"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Caption */}
      <div className={`mt-2 ${SIZE_CLASSES[block.width]} ${ALIGNMENT_CLASSES[block.alignment]}`}>
        {isEditing ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Add a caption..."
              className="flex-1 px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-accent"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCaptionSave();
                if (e.key === 'Escape') setIsEditing(false);
              }}
            />
            <button
              onClick={handleCaptionSave}
              className="px-3 py-1.5 text-sm bg-accent text-white rounded-lg hover:bg-accent-dark"
            >
              Save
            </button>
          </div>
        ) : (
          <p
            className={`text-sm text-gray-500 italic text-${block.alignment} cursor-pointer hover:text-gray-700`}
            onClick={() => setIsEditing(true)}
          >
            {block.caption || 'Click to add caption...'}
          </p>
        )}
      </div>
    </div>
  );
}

export default ImageBlock;
